import { useState } from "react";
import LeadStatusBadge from "./LeadStatusBadge";
import { updateSmsLead } from "@services/smsLeads";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function LeadInfoHeader({ lead, onUpdate }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);

  const canChangeStatus = !["DO_NOT_SEND", "BLOCKED"].includes(lead.status);

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      await updateSmsLead(lead.id, { status: newStatus });
      onUpdate?.();
    } catch (err) {
      alert(err.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await updateSmsLead(lead.id, { notes });
      setNotesChanged(false);
      onUpdate?.();
    } catch (err) {
      alert(err.message || "Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-gray-200 bg-white px-4 py-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="text-lg font-semibold text-gray-900">{lead.phone_number}</h2>
        <LeadStatusBadge status={lead.status} />
        <span className="text-sm text-gray-500">
          Step {lead.current_step}/4
        </span>
        {lead.next_send_at && (
          <span className="text-sm text-gray-500">
            Next: {formatDate(lead.next_send_at)}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        {lead.conversation_url && (
          <a
            href={lead.conversation_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline"
          >
            View Original Call
          </a>
        )}
        {lead.form_submitted && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
            Form Submitted
          </span>
        )}
        <span className="text-xs text-gray-400">
          Created {formatDate(lead.created_at)}
        </span>
      </div>

      {/* Status controls */}
      {canChangeStatus && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600">Change status:</span>
          {lead.status === "IN_SEQUENCE" && (
            <button
              onClick={() => handleStatusChange("MANUAL_STOP")}
              disabled={saving}
              className="px-3 py-1 text-xs font-medium rounded-lg bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:opacity-50"
            >
              Stop Sequence
            </button>
          )}
          {lead.status === "MANUAL_STOP" && (
            <button
              onClick={() => handleStatusChange("IN_SEQUENCE")}
              disabled={saving}
              className="px-3 py-1 text-xs font-medium rounded-lg bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50"
            >
              Resume Sequence
            </button>
          )}
          {!["BLOCKED", "DO_NOT_SEND"].includes(lead.status) && (
            <button
              onClick={() => handleStatusChange("BLOCKED")}
              disabled={saving}
              className="px-3 py-1 text-xs font-medium rounded-lg bg-orange-100 text-orange-800 hover:bg-orange-200 disabled:opacity-50"
            >
              Block
            </button>
          )}
        </div>
      )}

      {lead.form_submission && (
        <div className="mb-3 border border-gray-200 rounded-lg bg-gray-50 px-3 py-2">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
            Form Response
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm">
            {lead.form_submission.full_name && (
              <div>
                <dt className="text-xs text-gray-500">Name</dt>
                <dd className="text-gray-900">{lead.form_submission.full_name}</dd>
              </div>
            )}
            {lead.form_submission.clinic_name && (
              <div>
                <dt className="text-xs text-gray-500">Clinic</dt>
                <dd className="text-gray-900">{lead.form_submission.clinic_name}</dd>
              </div>
            )}
            {lead.form_submission.email && (
              <div>
                <dt className="text-xs text-gray-500">Email</dt>
                <dd className="text-gray-900 break-all">
                  <a
                    href={`mailto:${lead.form_submission.email}`}
                    className="text-blue-600 hover:underline"
                  >
                    {lead.form_submission.email}
                  </a>
                </dd>
              </div>
            )}
          </dl>
          <div className="text-xs text-gray-400 mt-1">
            Submitted {formatDate(lead.form_submission.created_at)}
          </div>
        </div>
      )}

      {lead.status === "DO_NOT_SEND" && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          This contact sent STOP. No messages can be sent. This is permanent (TCPA compliance).
        </div>
      )}

      {/* Notes */}
      <div className="flex items-start gap-2">
        <textarea
          className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Add notes..."
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setNotesChanged(true);
          }}
        />
        {notesChanged && (
          <button
            onClick={handleSaveNotes}
            disabled={saving}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
}
